import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { KeyRound, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { Input } from "@/components/ui/input";

export default function SetupAccount() {
  const navigate = useNavigate();
  const [state, setState] = useState("loading"); // loading | ready | invalid | done
  const [info, setInfo] = useState(null);
  const [secret, setSecret] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);

  const token = new URLSearchParams(window.location.search).get("token") || "";

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    api.get(`/auth/token-info?token=${encodeURIComponent(token)}`)
      .then((r) => { if (r.data.valid) { setInfo(r.data); setState("ready"); } else setState("invalid"); })
      .catch(() => setState("invalid"));
  }, [token]);

  const isPin = info?.credential === "pin";
  const submit = async (e) => {
    e.preventDefault();
    if (isPin && !/^\d{4,}$/.test(secret)) { toast.error("PIN must be at least 4 digits"); return; }
    if (!isPin && secret.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (secret !== confirm) { toast.error(`Those ${isPin ? "PINs" : "passwords"} don't match`); return; }
    setBusy(true);
    try {
      await api.post("/auth/complete-account", { token, secret });
      setState("done");
      toast.success(`Your ${isPin ? "PIN" : "password"} is set — you can sign in now`);
    } catch (err) { toast.error(err.response?.data?.detail || "Something went wrong"); }
    setBusy(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-6 bg-background">
      <div className="w-full max-w-md bg-white rounded-3xl border border-gray-100 p-8" data-testid="setup-account-page">
        <div className="flex items-center gap-2.5 mb-6">
          <img src="/logo.png" alt="Studio V" className="w-10 h-10 rounded-xl object-contain" />
          <span className="font-head font-extrabold text-2xl">Studio V</span>
        </div>

        {state === "loading" && <p className="text-muted-foreground">Checking your link…</p>}

        {state === "invalid" && (
          <div data-testid="setup-invalid">
            <h2 className="font-head text-2xl font-bold">This link isn't valid</h2>
            <p className="text-muted-foreground mt-2">It may have expired or already been used. Ask your studio to send a new one.</p>
            <button onClick={() => navigate("/login")} className="mt-6 bg-teal hover:bg-teal-dark text-white rounded-full py-3 px-6 font-semibold">Go to sign in</button>
          </div>
        )}

        {state === "ready" && (
          <>
            <div className="flex items-center gap-2 text-teal-dark"><KeyRound className="w-5 h-5" /><span className="font-semibold capitalize">{info.kind === "setup" ? "Set up your login" : `Reset your ${isPin ? "PIN" : "password"}`}</span></div>
            <h2 className="font-head text-2xl font-bold mt-2">Hi {info.name || "there"}!</h2>
            <p className="text-muted-foreground mt-1.5">{isPin ? <>Choose a secret PIN for username <strong>{info.username}</strong>.</> : "Choose a new password for your account."}</p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div>
                <label className="text-sm font-semibold">{isPin ? "New PIN (min 4 digits)" : "New password (min 6 characters)"}</label>
                <Input data-testid="setup-secret" type="password" inputMode={isPin ? "numeric" : "text"} value={secret} onChange={(e) => setSecret(e.target.value)} className="mt-1.5 rounded-xl" placeholder={isPin ? "••••" : "••••••••"} autoFocus />
              </div>
              <div>
                <label className="text-sm font-semibold">Confirm {isPin ? "PIN" : "password"}</label>
                <Input data-testid="setup-confirm" type="password" inputMode={isPin ? "numeric" : "text"} value={confirm} onChange={(e) => setConfirm(e.target.value)} className="mt-1.5 rounded-xl" />
              </div>
              <button type="submit" data-testid="setup-submit" disabled={busy} className="w-full bg-teal hover:bg-teal-dark text-white rounded-full py-3.5 font-semibold transition-colors">{busy ? "Saving…" : `Save ${isPin ? "PIN" : "password"}`}</button>
            </form>
          </>
        )}

        {state === "done" && (
          <div data-testid="setup-done">
            <div className="flex items-center gap-2 text-teal-dark"><ShieldCheck className="w-6 h-6" /><span className="font-head font-bold text-xl">All set!</span></div>
            <p className="text-muted-foreground mt-2">You can now sign in{info?.username ? <> with username <strong>{info.username}</strong></> : ""}.</p>
            <button onClick={() => navigate("/login")} data-testid="setup-goto-login" className="mt-6 bg-teal hover:bg-teal-dark text-white rounded-full py-3 px-6 font-semibold">Go to sign in</button>
          </div>
        )}
      </div>
    </div>
  );
}
